import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { type Condition } from 'features/libra/sections/formLayout'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'

type Primitive = string | number | boolean
type PrimitiveArray = ReadonlyArray<Primitive>

function normalizeString(value: string): string {
  return value.toLowerCase().trim().replace(/\s+/g, '')
}

function compareStringOrNumber(
  fieldValue: unknown,
  value: Condition['value'],
  compareFn: (a: number | string, b: number | string) => boolean
): boolean {
  if (
    (typeof fieldValue === 'number' && typeof value === 'number') ||
    (typeof fieldValue === 'string' && typeof value === 'string')
  ) {
    const lhs =
      typeof fieldValue === 'string' ? normalizeString(fieldValue) : fieldValue
    const rhs = typeof value === 'string' ? normalizeString(value) : value
    return compareFn(lhs, rhs)
  }
  return false
}

function isPrimitive(value: unknown): value is Primitive {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
}

function isPrimitiveArray(value: unknown): value is PrimitiveArray {
  return Array.isArray(value) && value.every(isPrimitive)
}

function isIn(fieldValue: unknown, value: Condition['value']): boolean {
  if (isPrimitiveArray(value)) {
    const normalizedValue = value.map((item) =>
      typeof item === 'string' ? normalizeString(item) : item
    ) as PrimitiveArray
    if (isPrimitiveArray(fieldValue)) {
      const normalizedFieldValue = fieldValue.map((item) =>
        typeof item === 'string' ? normalizeString(item) : item
      ) as PrimitiveArray
      return normalizedFieldValue.every((item) =>
        normalizedValue.includes(item)
      )
    }
    if (isPrimitive(fieldValue)) {
      const normalizedFieldValue =
        typeof fieldValue === 'string'
          ? normalizeString(fieldValue)
          : fieldValue
      return normalizedValue.includes(
        normalizedFieldValue as string | number | boolean
      )
    }
  } else if (typeof fieldValue === 'string' && typeof value === 'string') {
    return normalizeString(fieldValue).includes(normalizeString(value))
  }
  return false
}

type Comparator = (fieldValue: unknown, value: Condition['value']) => boolean

const valueComparators: Record<Condition['op'], Comparator> = {
  '=': (fieldValue, value) => {
    if (isPrimitiveArray(fieldValue) && isPrimitiveArray(value)) {
      return isEqual(
        fieldValue.map((item) =>
          typeof item === 'string' ? normalizeString(item) : item
        ) as PrimitiveArray,
        value.map((item) =>
          typeof item === 'string' ? normalizeString(item) : item
        ) as PrimitiveArray
      )
    }
    const normalizedFieldValue = Array.isArray(fieldValue)
      ? undefined
      : typeof fieldValue === 'string'
        ? normalizeString(fieldValue)
        : fieldValue
    const normalizedValue = Array.isArray(value)
      ? undefined
      : typeof value === 'string'
        ? normalizeString(value)
        : value
    return isEqual(normalizedFieldValue, normalizedValue)
  },
  '!=': (fieldValue, value) => {
    if (isPrimitiveArray(fieldValue) && isPrimitiveArray(value)) {
      return !isEqual(
        fieldValue.map((item) =>
          typeof item === 'string' ? normalizeString(item) : item
        ) as PrimitiveArray,
        value.map((item) =>
          typeof item === 'string' ? normalizeString(item) : item
        ) as PrimitiveArray
      )
    }
    const normalizedFieldValue = Array.isArray(fieldValue)
      ? undefined
      : typeof fieldValue === 'string'
        ? normalizeString(fieldValue)
        : fieldValue
    const normalizedValue = Array.isArray(value)
      ? undefined
      : typeof value === 'string'
        ? normalizeString(value)
        : value
    return !isEqual(normalizedFieldValue, normalizedValue)
  },
  '>': (fieldValue, value) =>
    compareStringOrNumber(fieldValue, value, (a, b) => a > b),
  '<': (fieldValue, value) =>
    compareStringOrNumber(fieldValue, value, (a, b) => a < b),
  '>=': (fieldValue, value) =>
    compareStringOrNumber(fieldValue, value, (a, b) => a >= b),
  '<=': (fieldValue, value) =>
    compareStringOrNumber(fieldValue, value, (a, b) => a <= b),
  in: (fieldValue, value) => isIn(fieldValue, value),
  not_in: (fieldValue, value) => !isIn(fieldValue, value),
  is_null: (fieldValue) => isNil(fieldValue),
  is_not_null: (fieldValue) => !isNil(fieldValue),
  is_truthy: (fieldValue) => Boolean(fieldValue),
  is_falsy: (fieldValue) => !fieldValue,
}

export function evaluateCondition(
  condition: Condition,
  fieldValues: FieldValues
): boolean {
  const { field, op, value } = condition
  // Unwrap from { value, source } to get the inner value
  const fieldValue = unwrapFieldValue(fieldValues[field])

  const comparator = valueComparators[op]
  return comparator(fieldValue, value)
}

/** All conditions must hold. An empty list is vacuously true — "always". */
export function evaluateConditions(
  conditions: ReadonlyArray<Condition>,
  fieldValues: FieldValues
): boolean {
  return conditions.every((condition) =>
    evaluateCondition(condition, fieldValues)
  )
}
